/**
 * Reserved-seat status label shared by the My Games cards (DKPLAT-533).
 *
 * A reservation is an entry with zero picks. Until the user converts it, every
 * game type shows the same three-state label so the lobby reads consistently
 * next to apps/frontend's GameCard and apps/squares' ContestCard:
 *
 *   1. pre-live, questions NOT available → "Entry Reserved"
 *   2. pre-live, questions available     → "No Picks Submitted - Make Selections
 *      Now!" (emphasised — the user still has to act)
 *   3. locked / live / final             → "No Picks Submitted" (carried through
 *      to Recent so a zero score is explained)
 *
 * The three strings are DK copy — use verbatim.
 *
 * `questionsAvailable` must be the *reveal* signal, never a generic "contest is
 * open" flag: an open contest whose questions_posted_at has not elapsed yet is
 * state 1, not state 2. Callers derive it from the same gate the backend applies
 * (`areQuestionsRevealed`) — the gated questions array for Pick'em, the
 * `has_questions` field for Squares — and it must fail CLOSED when absent, since
 * an unknown signal promising "Make Selections Now!" is the bug this ticket fixes.
 */
export type ReservedEntryState = 'upcoming' | 'countdown' | 'live' | 'final';

export function getReservedStatusLabel(
  state: ReservedEntryState,
  questionsAvailable: boolean
): { text: string; emphasised: boolean } {
  // Contest locked / live / final — reserved but never picked, carried through to results.
  if (state === 'live' || state === 'final') {
    return { text: 'No Picks Submitted', emphasised: false };
  }
  // Upcoming/countdown, questions available — user must act.
  if (questionsAvailable) {
    return { text: 'No Picks Submitted - Make Selections Now!', emphasised: true };
  }
  // Upcoming/countdown, questions not yet available.
  return { text: 'Entry Reserved', emphasised: false };
}
